import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { Copy, FileJson, Info, ListCheck, Loader } from "lucide-react";
import {
  getBasicEChallan,
  getChallanProDetails,
  verifyPrefillv3Details,
} from "../../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isAlphanumeric, isNumeric } from "validator";

function BasicEChallan() {
  const { serviceName } = useService();

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [vehicleNumber, setVehicleNumber] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  const handleBasicEChallan = async () => {
    if (!profileData) return;

    const validate = isAlpha(vehicleNumber);

    if (!validate) {
      return setInputError(
        "⚠️ Please enter alpha numeric in vehicle No. field",
      );
    } else if (vehicleNumber.length < 5) {
      return setInputError("⚠️ Enter at least 5 digits in vehicle No. field");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getBasicEChallan({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        vehicle_num: vehicleNumber.trim(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14">
        {/* ---------------- FORM ---------------- */}
        <div className=" max-w-3xl border rounded-md sm:p-6 mt-6 bg-white">
          <div className="flex items-center flex-col flex-wrap gap-4">
            {/* Vehicle */}
            <div className="flex items-center gap-4">
              <label className="w-32 font-bold">
                Vehicle No <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                maxLength={15}
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                className="flex-1 border border-gray-400 rounded-md px-3 h-[38px]"
                placeholder="Eg: GJ08BN1256"
              />
            </div>

            {/* show input errors */}

            {inputError && (
              <div className="text-red-800 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex justify-end ">
              <button
                disabled={isLoading || !vehicleNumber}
                onClick={handleBasicEChallan}
                className="px-6 py-2 bg-gradient-to-br hover:bg-black active:bg-green-600 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {/* response message */}

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div className="w-full flex justify-center">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid vehicle number. Clicking the Verify button.
              After successful verification, the system will return the response
              in structured format, including contact details associated with
              the provided details. You may review and copy the data for your
              reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            <div className="bg-gray-200 p-5 rounded-md">
              <h2 className="text-2xl flex items-center justify-center font-bold text-blue-500 mb-4">
                E-Challan Details
              </h2>

              {/* -------- BASIC INFO -------- */}
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-semibold w-48 inline-block">
                    Message:
                  </span>
                  {response?.data?.message || "N/A"}
                </div>

                <div>
                  <span className="font-semibold w-48 inline-block">
                    Total Challan:
                  </span>
                  {response?.data?.totalChallan ?? "N/A"}
                </div>
              </div>

              {/* -------- CHALLANS -------- */}
              {response?.data?.results?.length > 0 &&
                response.data.results.map((item, index) => (
                  <div key={index} className="mt-6 border p-4 rounded bg-white">
                    <h3 className="font-bold text-lg mb-3 text-blue-600">
                      Challan #{index + 1}
                    </h3>

                    {/* BASIC DETAILS */}
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Owner Name:
                        </span>{" "}
                        {item.ownerName || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Amount:
                        </span>{" "}
                        ₹{item.amount ?? "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Challan No:
                        </span>{" "}
                        {item.challanNo || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Status:
                        </span>{" "}
                        {item.status || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Date Time:
                        </span>{" "}
                        {item.dateTime || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          State Code:
                        </span>{" "}
                        {item.stateCd || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Area:
                        </span>{" "}
                        {item.areaName || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Source:
                        </span>{" "}
                        {item.source || "N/A"}
                      </div>
                    </div>

                    {/* EXTRA DETAILS */}
                    <div className="mt-4 space-y-2 text-sm">
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Doc No:
                        </span>{" "}
                        {item.docNo || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Department Code:
                        </span>{" "}
                        {item.dptCd ?? "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          RTO Code:
                        </span>{" "}
                        {item.rtoCd ?? "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Designation:
                        </span>{" "}
                        {item.designation || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Transaction ID:
                        </span>{" "}
                        {item.transactionId || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Payment Date:
                        </span>{" "}
                        {item.paymentDate || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Payment Source:
                        </span>{" "}
                        {item.paymentSource || "N/A"}
                      </div>
                    </div>

                    {/* COURT DETAILS */}
                    <div className="mt-4 space-y-2 text-sm">
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Court Status:
                        </span>{" "}
                        {item.courtStatus ?? "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Court Name:
                        </span>{" "}
                        {item.courtName || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Court Address:
                        </span>{" "}
                        {item.courtAddress || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Sent To Court On:
                        </span>{" "}
                        {item.sentToCourtOn || "N/A"}
                      </div>
                    </div>

                    {/* POLICE / OFFICE */}
                    <div className="mt-4 space-y-2 text-sm">
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Traffic Police:
                        </span>{" "}
                        {item.trafficPolice ?? "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Office Name:
                        </span>{" "}
                        {item.officeName || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Office Text:
                        </span>{" "}
                        {item.officeText || "N/A"}
                      </div>
                    </div>

                    {/* FLAGS */}
                    <div className="mt-4 space-y-2 text-sm">
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Vehicle Impound:
                        </span>{" "}
                        {item.vehicleImpound || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          VCourt Transfer:
                        </span>{" "}
                        {item.vcourtTransferredToCourt ?? "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Sent To VCourt:
                        </span>{" "}
                        {item.sentToVcourt ?? "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Valid Contact No:
                        </span>{" "}
                        {item.validContactNo ?? "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Payment Eligible:
                        </span>{" "}
                        {item.paymentEligible ?? "N/A"}
                      </div>
                    </div>

                    {/* URLS */}
                    <div className="mt-4 space-y-2 text-sm">
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          PDF URL:
                        </span>{" "}
                        {item.pdfUrl || "N/A"}
                      </div>
                      <div>
                        <span className="font-semibold w-48 inline-block">
                          Receipt URL:
                        </span>{" "}
                        {item.receiptUrl || "N/A"}
                      </div>
                    </div>

                    {/* OFFENCES */}
                    <div className="mt-6">
                      <h4 className="font-bold mb-2">Offences</h4>

                      {item.offences?.length > 0 ? (
                        item.offences.map((off, i) => (
                          <div
                            key={i}
                            className="border p-3 rounded-xl mb-2 bg-rose-700 text-white"
                          >
                            <div>
                              <b>Offence Name:</b> {off.offenceName || "N/A"}
                            </div>
                            <div>
                              <b>Offence ID:</b> {off.offenceId || "N/A"}
                            </div>
                            <div>
                              <b>MVA:</b> {off.mva || "N/A"}
                            </div>
                            <div>
                              <b>MVA1:</b> {off.mva1 || "N/A"}
                            </div>
                            <div>
                              <b>Penalty:</b> {off.penalty || "N/A"}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div>N/A</div>
                      )}
                    </div>

                    {/* CUSTOM OFFENCE */}
                    <div className="mt-4">
                      <h4 className="font-bold mb-2">Custom Offence</h4>
                      {item.customOffence?.length > 0 ? (
                        item.customOffence.map((c, i) => <div key={i}>{c}</div>)
                      ) : (
                        <div>N/A</div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default BasicEChallan;
